import React, { useState, FC } from 'react'
import { useIntl } from 'react-intl'
import { useFormContext } from 'react-hook-form'
import { UseFormReturn } from 'react-hook-form/dist/types'
import { css, cx } from '@emotion/css'
import {
  IconClose,
  IconCaretLeft,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { Text, Link } from '@contrast-security-inc/design-system-typography'
import { ConfirmOnExitDialog } from './footer/confirm-on-exit-dialog'
import { useJiraIntegrationPage } from './jira-integration-page.provider'
import {
  cancelAndRestorePreviousCredentials,
  updateManagingCredentials
} from './state/jira-integration-page-actions'
import {
  ConnectionStatus,
  hasManageCredentialsDraftChanges,
  JiraPageState,
  updateAnyState
} from './state/jira-integration-page.state'
import { JiraIntegrationForm } from './jira-form-schema'
import { Box, Row } from '../../../../legacy-design-system/form'
import { getIntegrationsRoute } from '../../../route'
import { formatStyles } from '../../../react-intl-helpers'

const Title: FC<{
  isManagingCredentials: boolean
}> = ({ isManagingCredentials }) => {
  const intl = useIntl()
  const messageId = isManagingCredentials
    ? 'title_integrationsSettings_credentials'
    : 'title_connection'

  return (
    <div
      className={css`
        display: flex;
        align-items: center;
        > img {
          margin-left: 3px;
          height: 33px;
        }
      `}
    >
      <img src="images/integrations/jiraLogo.png" alt={'Jira Logo'} />{' '}
      <Text font="titleH6">{intl.formatMessage({ id: messageId })}</Text>
    </div>
  )
}

const BackButton: FC<{
  isManagingCredentials: boolean
  setManagingCredentials: (boolean) => void
  setIsCancelModalOpen: (boolean) => void
  connectionStatus: ConnectionStatus
  resetCredentials: () => void
  formContext: UseFormReturn<JiraIntegrationForm>
  pageState: Partial<JiraPageState>
}> = ({
  isManagingCredentials,
  setManagingCredentials,
  connectionStatus,
  resetCredentials,
  setIsCancelModalOpen,
  formContext,
  pageState
}) => {
  const intl = useIntl()

  if (!isManagingCredentials) {
    return null
  }

  return (
    <div
      className={css`
        display: flex;
        align-items: center;
        margin-left: 20px;
      `}
    >
      <IconCaretLeft />
      <Link
        testId="jira-integration-back-to-connection-link"
        onClick={() => {
          if (
            hasManageCredentialsDraftChanges(formContext, pageState) &&
            connectionStatus === 'valid'
          ) {
            setIsCancelModalOpen(true)
          } else {
            resetCredentials()
            setManagingCredentials(false)
          }
        }}
      >
        <Text font="metadata">
          {intl.formatMessage({
            id: 'action_integrationsSettings_backToConnection'
          })}
        </Text>
      </Link>
    </div>
  )
}
export const Header = () => {
  const intl = useIntl()
  const { state: pageState, dispatch } = useJiraIntegrationPage()

  const {
    errorCode,
    errorMessages,
    fieldErrors,
    isManagingCredentials,
    connectionStatus
  } = pageState

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const formContext = useFormContext<JiraIntegrationForm>()

  const setManagingCredentials = (value) =>
    updateManagingCredentials({
      dispatch,
      formContext,
      pageState,
      isManagingCredentials: value
    })

  const resetCredentials = () =>
    cancelAndRestorePreviousCredentials({
      dispatch,
      formContext,
      pageState
    })

  return (
    <Row>
      <ConfirmOnExitDialog
        isOpen={isCancelModalOpen}
        onCancel={() => {
          setIsCancelModalOpen(false)
          setManagingCredentials(false)
          resetCredentials()
        }}
        onConfirm={() => {
          dispatch(
            updateAnyState({
              hasCredentialsUpdate: true
            })
          )
          setIsCancelModalOpen(false)
          setManagingCredentials(false)
        }}
      />
      <Box
        basis={12}
        className={cx(
          'jira-integration-header',
          css`
            display: flex;
            height: 70px;
            justify-content: space-between;
            margin-bottom: 15px;
            border-bottom: 1px solid ${theme.color.neutral50};
          `
        )}
      >
        <div
          className={css`
            flex-grow: 1;
          `}
        >
          <Title isManagingCredentials={isManagingCredentials} />
          <BackButton
            isManagingCredentials={isManagingCredentials}
            setIsCancelModalOpen={setIsCancelModalOpen}
            setManagingCredentials={setManagingCredentials}
            connectionStatus={connectionStatus}
            resetCredentials={resetCredentials}
            pageState={pageState}
            formContext={formContext}
          />
        </div>
        <div>
          <a
            href={getIntegrationsRoute()}
            className={css`
              cursor: pointer;
            `}
          >
            <IconClose color="neutral50" />
          </a>
        </div>
      </Box>
      {(errorCode || errorMessages.length > 0 || fieldErrors.length > 0) && (
        <Box
          basis={12}
          className={css`
            color: ${theme.color.red50};
          `}
        >
          {errorCode && (
            <div>
              <Text font="body">
                {intl.formatMessage(
                  { id: 'variable_integrationsSettings_jiraErrorCode_react' },
                  {
                    errorStatusCode: errorCode,
                    ...formatStyles.bold
                  }
                )}
              </Text>
            </div>
          )}
          {(errorMessages.length > 0 || fieldErrors.length > 0) && (
            <div>
              <br />
              <Text font="body">
                {intl.formatMessage({
                  id: 'detail_integrationsSettings_errorTestingConnection'
                })}
              </Text>
              <ul>
                {errorMessages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
                {fieldErrors.map((fieldError) => (
                  <li key={fieldError.message}>{fieldError.message}</li>
                ))}
              </ul>
            </div>
          )}
        </Box>
      )}
    </Row>
  )
}
